import React, { useState } from "react";
import {
  Box, Typography, TextField, MenuItem, FormControlLabel, Switch, Button, Alert,
} from "@mui/material";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import { NAVY, TEXT_SECONDARY } from "../../../theme/tokens";
import {
  WebsiteConfig, REFRESH_INTERVALS, WEBSITE_FITS,
  AUTO_SCROLL_SPEEDS, AUTO_SCROLL_DIRECTIONS, AUTO_SCROLL_END_BEHAVIORS,
  AUTO_SCROLL_DELAYS,
  readWebsiteConfig, validateWebsiteInput, normalizeProviderUrl,
} from "./websiteConfig";

/**
 * Website / URL inspector — normal web pages only. Video, map and generic
 * embed content are now their own palette widgets (Video / Map / Embed),
 * so this panel no longer carries a "display mode" selector.
 *
 * All operator input is normalized + sanitized before it reaches
 * widget_config — only a safe http(s) `url` is ever stored. A pasted provider
 * page (e.g. YouTube) is still normalized to its embeddable URL so it loads.
 */
interface Props {
  cfg: Record<string, any>;
  setCfg: (patch: Record<string, any>) => void;
  interacting: boolean;
  onToggleInteract: () => void;
}

const WebsiteInspector: React.FC<Props> = ({ cfg: rawCfg, setCfg, interacting, onToggleInteract }) => {
  const cfg: WebsiteConfig = readWebsiteConfig(rawCfg);
  const [draft, setDraft] = useState<string>(cfg.url ?? "");

  const validationMsg = validateWebsiteInput({ ...cfg, display_mode: "website", input_type: "url" }, draft);

  const commitSource = (value: string) => {
    setDraft(value);
    const s = value.trim();
    if (!s) { setCfg({ url: "", display_mode: "website" }); return; }
    // Normalize known provider pages to an embeddable URL, and always pin
    // website mode so this stays a plain-page zone.
    setCfg({ url: normalizeProviderUrl(s, "website"), display_mode: "website" });
  };

  return (
    <Box>
      <TextField
        size="small" fullWidth label="Website URL *"
        placeholder="https://example.com"
        value={draft}
        onChange={(e) => commitSource(e.target.value)}
        error={!!validationMsg}
        helperText={validationMsg ?? undefined}
        sx={{ mb: 1.5 }}
      />

      <TextField
        select size="small" fullWidth label="Refresh interval"
        value={cfg.refresh_interval}
        onChange={(e) => setCfg({ refresh_interval: Number(e.target.value) })}
        sx={{ mb: 1.5 }}
      >
        {REFRESH_INTERVALS.map((r) => (
          <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
        ))}
      </TextField>

      <TextField
        select size="small" fullWidth label="Fit mode"
        value={cfg.fit}
        onChange={(e) => setCfg({ fit: e.target.value })}
        sx={{ mb: 1.5 }}
      >
        {WEBSITE_FITS.map((f) => (
          <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
        ))}
      </TextField>

      <TextField
        size="small" fullWidth type="color" label="Background colour"
        value={cfg.background ?? "#000000"}
        onChange={(e) => setCfg({ background: e.target.value })}
        sx={{ mb: 1.5 }}
      />

      {/* ── Behaviour ── */}
      <SectionLabel>Behaviour</SectionLabel>
      <Toggle label="Show scrollbars" checked={cfg.show_scrollbars} onChange={(v) => setCfg({ show_scrollbars: v })} />
      <Toggle label="Allow interaction" checked={cfg.allow_interaction} onChange={(v) => setCfg({ allow_interaction: v })} />
      <Toggle label="Allow fullscreen" checked={cfg.allow_fullscreen} onChange={(v) => setCfg({ allow_fullscreen: v })} />
      <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, mt: 0.5 }}>
        “Show scrollbars” only displays the scrollbar. For unattended TVs with no
        mouse or remote, use Auto-scroll below to move through a long page.
      </Typography>

      {/* ── Auto-scroll ── */}
      <SectionLabel>Auto-scroll</SectionLabel>
      <TextField
        select size="small" fullWidth label="Auto-scroll"
        value={cfg.auto_scroll_enabled ? cfg.auto_scroll_speed : "off"}
        onChange={(e) => {
          const speed = e.target.value;
          setCfg({ auto_scroll_speed: speed, auto_scroll_enabled: speed !== "off" });
        }}
        sx={{ mb: 1 }}
      >
        {AUTO_SCROLL_SPEEDS.map((s) => (
          <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
        ))}
      </TextField>

      {cfg.auto_scroll_enabled && (
        <>
          {cfg.auto_scroll_speed === "custom" && (
            <TextField
              size="small" fullWidth type="number" label="Custom speed (px / sec)"
              inputProps={{ min: 1, max: 2000, step: 5 }}
              value={cfg.auto_scroll_custom_speed ?? 40}
              onChange={(e) => setCfg({ auto_scroll_custom_speed: Math.max(1, Math.min(2000, Number(e.target.value) || 0)) })}
              sx={{ mb: 1 }}
            />
          )}
          <TextField
            select size="small" fullWidth label="Direction"
            value={cfg.auto_scroll_direction}
            onChange={(e) => setCfg({ auto_scroll_direction: e.target.value })}
            sx={{ mb: 1 }}
          >
            {AUTO_SCROLL_DIRECTIONS.map((d) => (
              <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            select size="small" fullWidth label="At end of page"
            value={cfg.auto_scroll_end_behavior}
            onChange={(e) => setCfg({ auto_scroll_end_behavior: e.target.value })}
            sx={{ mb: 1 }}
          >
            {AUTO_SCROLL_END_BEHAVIORS.map((b) => (
              <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>
            ))}
          </TextField>
          <DelayField
            label="Start delay"
            value={cfg.auto_scroll_start_delay_ms}
            onChange={(v) => setCfg({ auto_scroll_start_delay_ms: v })}
          />
          <DelayField
            label="Pause at end"
            value={cfg.auto_scroll_end_pause_ms}
            onChange={(v) => setCfg({ auto_scroll_end_pause_ms: v })}
          />
          {cfg.fit !== "native" && (
            <Alert severity="info" sx={{ mt: 0.5, mb: 1, fontSize: 12, py: 0.5 }}>
              Auto-scroll needs page overflow — set Fit mode to “Native / Scroll”
              (or a tall viewport height) so there is content to scroll through.
            </Alert>
          )}
          <Alert severity="warning" sx={{ mt: 0.5, fontSize: 12, py: 0.5 }}>
            Automatic scrolling may not be supported by this website. Some sites use
            fixed layouts or nested scroll areas that cannot be scrolled from outside.
          </Alert>
        </>
      )}

      {/* ── Advanced viewport ── */}
      <SectionLabel>Advanced viewport</SectionLabel>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 1 }}>
        <TextField
          size="small" type="number" label="Viewport W (px)"
          placeholder="auto"
          value={cfg.viewport_width ?? ""}
          onChange={(e) => setCfg({ viewport_width: e.target.value ? Math.max(100, Number(e.target.value)) : null })}
        />
        <TextField
          size="small" type="number" label="Viewport H (px)"
          placeholder="auto"
          value={cfg.viewport_height ?? ""}
          onChange={(e) => setCfg({ viewport_height: e.target.value ? Math.max(100, Number(e.target.value)) : null })}
        />
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 1 }}>
        <TextField
          size="small" type="number" label="Page zoom (%)"
          inputProps={{ min: 25, max: 400, step: 5 }}
          value={cfg.page_zoom ?? 100}
          onChange={(e) => setCfg({ page_zoom: Math.max(25, Math.min(400, Number(e.target.value) || 100)) })}
        />
        <TextField
          select size="small" label="User agent"
          value={cfg.user_agent_mode}
          onChange={(e) => setCfg({ user_agent_mode: e.target.value })}
        >
          <MenuItem value="desktop">Desktop</MenuItem>
          <MenuItem value="mobile">Mobile</MenuItem>
        </TextField>
      </Box>
      <TextField
        size="small" fullWidth type="number" label="Initial scroll position (px)"
        inputProps={{ min: 0, step: 10 }}
        value={cfg.initial_scroll_position ?? 0}
        onChange={(e) => setCfg({ initial_scroll_position: Math.max(0, Number(e.target.value) || 0) })}
        sx={{ mb: 1 }}
      />
      <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, mb: 1 }}>
        Viewport size, zoom and user agent emulate how the page is rendered.
        User agent is applied on the Android player; the editor preview uses the browser default.
      </Typography>

      <Button
        fullWidth size="small" variant={interacting ? "contained" : "outlined"}
        color={interacting ? "secondary" : "primary"}
        startIcon={<TouchAppIcon />}
        onClick={onToggleInteract}
        sx={{ mt: 1 }}
      >
        {interacting ? "Return to editing" : "Interact with website"}
      </Button>
      {interacting && (
        <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, mt: 0.75 }}>
          The website is now clickable and scrollable on the canvas. Dragging and
          resizing are paused — choose “Return to editing” to move or resize the zone again.
        </Typography>
      )}
    </Box>
  );
};

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontSize: 11, fontWeight: 700, color: NAVY, mt: 1, mb: 0.5,
    textTransform: "uppercase", letterSpacing: "0.06em" }}>
    {children}
  </Typography>
);

const Toggle: React.FC<{ label: string; checked: boolean; onChange: (v: boolean) => void }> = ({
  label, checked, onChange,
}) => (
  <FormControlLabel
    control={<Switch size="small" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />}
    label={<Typography sx={{ fontSize: 13 }}>{label}</Typography>}
    sx={{ display: "flex", ml: 0, mb: 0.25 }}
  />
);

/**
 * A delay/pause picker with preset seconds plus a Custom option that reveals
 * a millisecond number field. Values are stored in milliseconds.
 */
const DelayField: React.FC<{ label: string; value: number; onChange: (ms: number) => void }> = ({
  label, value, onChange,
}) => {
  const isPreset = AUTO_SCROLL_DELAYS.some((d) => d.value === value && d.value >= 0);
  const selectValue = isPreset ? value : -1;
  return (
    <Box sx={{ mb: 1 }}>
      <TextField
        select size="small" fullWidth label={label}
        value={selectValue}
        onChange={(e) => {
          const v = Number(e.target.value);
          onChange(v === -1 ? Math.max(0, value || 0) : v);
        }}
      >
        {AUTO_SCROLL_DELAYS.map((d) => (
          <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
        ))}
      </TextField>
      {selectValue === -1 && (
        <TextField
          size="small" fullWidth type="number" label={`${label} (ms)`}
          inputProps={{ min: 0, step: 500 }}
          value={value ?? 0}
          onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))}
          sx={{ mt: 1 }}
        />
      )}
    </Box>
  );
};

export default WebsiteInspector;
